import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';

type GateStatus = 'OK' | 'WARN' | 'ERROR' | 'NONE';
type RatingLetter = 'A' | 'B' | 'C' | 'D' | 'E';

/** One project's measures, as the backend's formatMetricsForDashboard gives them. */
interface ProjectMetrics {
  bugs: number;
  vulnerabilities: number;
  codeSmells: number;
  securityHotspots: number;
  coverage: number;
  duplicatedLines: number;
  // SonarQube's 1 (A) to 5 (E); 0 when the project has not been rated.
  maintainabilityRating: number;
  reliabilityRating: number;
  securityRating: number;
}

interface ProjectReport {
  project: string;
  metrics: ProjectMetrics | null;
}

/** GET /api/sysdevops/quality */
interface QualityData {
  overall: GateStatus;
  projects: Record<string, GateStatus>;
  // The worst rating across projects, or null when none was rated.
  metrics: {
    maintainability: RatingLetter | null;
    reliability: RatingLetter | null;
    security: RatingLetter | null;
  };
  sonarqubeData: ProjectReport[];
}

const RATING_LETTERS: RatingLetter[] = ['A', 'B', 'C', 'D', 'E'];

const letterFor = (rating: number): RatingLetter | null => RATING_LETTERS[Math.round(rating) - 1] ?? null;

const ratingColor = (letter: RatingLetter | null) => {
  if (letter === 'A') return 'text-scarf-green';
  if (letter === 'B') return 'text-brand-blue-light';
  if (letter) return 'text-brand-red';
  return 'text-gray-400';
};

const GATE_STYLES: Record<GateStatus, { label: string; className: string }> = {
  OK: { label: 'Passed', className: 'bg-scarf-green/20 text-scarf-green' },
  WARN: { label: 'Warning', className: 'bg-brand-blue-light/20 text-brand-blue-light' },
  ERROR: { label: 'Failed', className: 'bg-brand-red/20 text-brand-red' },
  NONE: { label: 'No gate', className: 'bg-gray-100 text-gray-600' },
};

const GateBadge: React.FC<{ status: GateStatus }> = ({ status }) => {
  const { label, className } = GATE_STYLES[status] ?? GATE_STYLES.NONE;
  return <span className={`px-2 py-1 rounded text-xs font-bold ${className}`}>{label}</span>;
};

const Rating: React.FC<{ letter: RatingLetter | null }> = ({ letter }) => (
  <span className={`font-bold ${ratingColor(letter)}`}>{letter ?? '–'}</span>
);

/**
 * Code quality, from SonarQube.
 *
 * This tab used to show three hardcoded projects, from an old setup that no
 * longer exists, with invented scores and a made-up issue list. It now shows
 * what /quality reports: each project SonarQube has, its quality gate, and
 * its measures. Individual issues are on the SonarQube tab.
 */
const QualityComponent: React.FC = () => {
  const [quality, setQuality] = useState<QualityData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchQualityData();
  }, []);

  const fetchQualityData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.getQualityMetrics();
      if (response.success && response.data) {
        setQuality(response.data as QualityData);
      } else {
        setError(response.message || 'Could not load quality data');
      }
    } catch (err) {
      // The server's own reason, e.g. "SonarQube is unavailable: …".
      setError(err instanceof Error ? err.message : 'Could not reach the server to load quality data');
    } finally {
      setIsLoading(false);
    }
  };

  const reports = quality?.sonarqubeData ?? [];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
          <h3 className="text-xl font-title font-bold text-deep-navy">Code Quality</h3>
          <button
            onClick={fetchQualityData}
            disabled={isLoading}
            className="bg-brand-red hover:bg-brand-red/90 disabled:opacity-50 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            {isLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {error && (
          <div role="alert" className="bg-brand-red/10 border border-brand-red/30 text-brand-red rounded-xl p-4">
            {error}
          </div>
        )}

        {!error && isLoading && !quality && <p className="text-gray-500">Loading quality data…</p>}

        {quality && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-gray-500 text-sm mb-1">Quality gate</p>
              <GateBadge status={quality.overall} />
            </div>
            <div>
              <p className="text-gray-500 text-sm mb-1">Maintainability</p>
              <Rating letter={quality.metrics.maintainability} />
            </div>
            <div>
              <p className="text-gray-500 text-sm mb-1">Reliability</p>
              <Rating letter={quality.metrics.reliability} />
            </div>
            <div>
              <p className="text-gray-500 text-sm mb-1">Security</p>
              <Rating letter={quality.metrics.security} />
            </div>
          </div>
        )}

        {quality && (
          <p className="text-gray-500 text-sm mt-4">
            Ratings are the worst across projects, A (best) to E. Individual issues are on the SonarQube tab.
          </p>
        )}
      </div>

      {reports.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reports.map(({ project, metrics }) => (
            <div key={project} className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
              <div className="flex justify-between items-start gap-2 mb-4">
                <h4 className="text-lg font-title font-bold text-deep-navy break-all">{project}</h4>
                <GateBadge status={quality?.projects[project] ?? 'NONE'} />
              </div>

              {metrics ? (
                <dl className="space-y-2">
                  {[
                    ['Bugs', metrics.bugs],
                    ['Vulnerabilities', metrics.vulnerabilities],
                    ['Code smells', metrics.codeSmells],
                    ['Security hotspots', metrics.securityHotspots],
                    ['Coverage', `${metrics.coverage}%`],
                    ['Duplicated lines', `${metrics.duplicatedLines}%`],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between">
                      <dt className="text-gray-600">{label}</dt>
                      <dd className="font-bold text-gray-800">{value}</dd>
                    </div>
                  ))}
                  {[
                    ['Maintainability', metrics.maintainabilityRating],
                    ['Reliability', metrics.reliabilityRating],
                    ['Security', metrics.securityRating],
                  ].map(([label, rating]) => (
                    <div key={label} className="flex justify-between">
                      <dt className="text-gray-600">{label}</dt>
                      <dd><Rating letter={letterFor(Number(rating))} /></dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="text-gray-500 text-sm">SonarQube has no measures for this project yet.</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default QualityComponent;
